import type {MutableRefObject, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import type {DragDirection, Range, ResizeEndEvent, ResizeMoveEvent, ResizeStartEvent} from '@Libraries/timeline/types';
import {useItem} from '@Libraries/timeline';
import {noop} from '@Utils/general.util';
import {Tooltip} from '@Components/tooltip';
import {DurationNotification} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/duration-notification/duration-notification';
import {TimelineItemDurationContainerType} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/duration-notification/duration-notification.types';
import {
  TimelineHandleSide,
  TimelineItemHandleType,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-item-handle/timeline-item-handle.types';
import TimelineItemHandle from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline-item-handle/timeline-item-handle';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {
  POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID,
  POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID,
  TIMELINE_ITEM_ACCEPTABLE_EDITING_WIDTH,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {TIMELINE_DESIGN_ITEM_ID} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {updateItemResizeState, updateTimelineWidthPerSecond} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {PageNumberNotification} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/page-number-notification/page-number-notification';
import styles from './base-timeline-item.module.scss';
import type {BaseTimelineItemProps} from './base-timeline-item.types';
import {TimelineItemType} from './base-timeline-item.types';
import {useAppDispatch, useAppSelector} from '@/hooks';

const THRESHOLD = 40;
const MOBILE_THRESHOLD = 75;
const MAX_ACCELERATION_VALUE = 3;
const DEFAULT_VALUE_FOR_TIMEOUT = 25;

export function BaseTimelineItem({
  onAutoScroll = noop,
  onResizeMove = noop,
  onResizeStart = noop,
  onResizeEnd = noop,
  type = TimelineItemType.Blue,
  durationItemType = TimelineItemDurationContainerType.Default,
  isSelected = false,
  onClick = noop,
  onClickAddButton = noop,
  isAddButtonDisabled = false,
  disabled = false,
  disableDragOnly = false,
  showAddButton = false,
  addItemIconSize = IconSize.SIZE_ICON_16,
  isHidden = false,
  isDesignItem = false,
  isMultiPageEnabled = false,
  ...props
}: BaseTimelineItemProps): ReactElement<any> {
  const rightHandleRef = useRef<HTMLDivElement>(null);
  const leftHandleRef = useRef<HTMLDivElement>(null);
  const updatedRelevanceWhileResizing = useRef<Range>(props.range);
  const isAnyItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isItemResizing;
  });
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  }>({left: 0, top: 0});
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const resizeDirection = useRef<string>('start');
  const rangeWhileResizing = useRef(props.range);
  const interval: MutableRefObject<NodeJS.Timeout | null> = useRef(null);
  const timer: MutableRefObject<NodeJS.Timeout | null> = useRef(null);
  const valueOffsetWithoutHittingResize = useRef(0);
  const previousDifference = useRef<null | number>(null);
  const isResizingOutOfBounds = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const threshold = isEditorMobileVariant() ? MOBILE_THRESHOLD : THRESHOLD;
  const isFirstRenderComplete = useRef(false);

  useEffect((): void => {
    setTimeout((): void => {
      isFirstRenderComplete.current = true;
    }, 200);
  }, []);

  const clearTimeoutIfExists = (): void => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  };
  const clearIntervalIfExists = (): void => {
    if (interval.current) {
      clearInterval(interval.current);
    }
  };

  const getAccelerationFromThreshold = (value: number): number => {
    return Math.min(MAX_ACCELERATION_VALUE, value / threshold);
  };

  const autoScrolling = (event: ResizeMoveEvent): void => {
    // eslint-disable-next-line no-restricted-globals
    const webBottomBarJqueryElement = $(`#${isEditorMobileVariant() ? POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID : POSTER_EDITOR_WEB_BOTTOM_BAR_CONTAINER_ID}`);
    const timelineContainer = webBottomBarJqueryElement?.find('#timeline-container').get(0);
    const timelineParentClientWidth = timelineContainer?.getBoundingClientRect().width;
    const timelineParentClientX = timelineContainer?.getBoundingClientRect().x;
    const range = event.active.data.current.getSpanFromResizeEvent?.(event);
    const itemLeft = node.current?.getBoundingClientRect().left;
    const itemRight = node.current?.getBoundingClientRect().right;

    if (!timelineParentClientWidth || !range || timelineParentClientX === undefined) {
      return;
    }

    if (event.direction === 'start' && itemLeft) {
      const isExceedingRightSide = timelineParentClientWidth - itemLeft < threshold;
      const isExceedingLeftSide = itemLeft < threshold;

      if (!isExceedingRightSide || !isExceedingLeftSide) {
        clearIntervalIfExists();
        clearTimeoutIfExists();
        previousDifference.current = null;
        isResizingOutOfBounds.current = false;
      }

      if (isExceedingRightSide) {
        isResizingOutOfBounds.current = true;
        const acceleration = getAccelerationFromThreshold(threshold - (timelineParentClientWidth - itemLeft));
        rangeWhileResizing.current = range;
        clearIntervalIfExists();
        clearTimeoutIfExists();

        timer.current = setTimeout((): void => {
          interval.current = setInterval((): void => {
            const currentIterationDifference = threshold - (timelineParentClientWidth - itemLeft);
            const pixelsAdding = 5 + (currentIterationDifference - (previousDifference.current ?? 0));
            const timeAdding = pixelsAdding / widthPerSecond;

            if (props.minDuration && rangeWhileResizing.current.end - rangeWhileResizing.current.start + valueOffsetWithoutHittingResize.current <= props.minDuration) {
              return;
            }

            onAutoScroll(timeAdding, 'start');
            timelineContainer.scrollLeft += pixelsAdding;
            valueOffsetWithoutHittingResize.current += timeAdding;
            previousDifference.current = currentIterationDifference;
          }, DEFAULT_VALUE_FOR_TIMEOUT / acceleration);
        }, 50);
      }

      if (isExceedingLeftSide && props.id !== TIMELINE_DESIGN_ITEM_ID) {
        isResizingOutOfBounds.current = true;
        const acceleration = getAccelerationFromThreshold(threshold - itemLeft);

        rangeWhileResizing.current = range;
        clearIntervalIfExists();
        clearTimeoutIfExists();

        timer.current = setTimeout((): void => {
          interval.current = setInterval((): void => {
            const currentIterationDifference = threshold - itemLeft;
            const pixelsSubtracting = 5 + (currentIterationDifference - (previousDifference.current ?? 0));
            const timeDecreasing = pixelsSubtracting / widthPerSecond;

            if (rangeWhileResizing.current.start < 0) {
              return;
            }

            if (rangeWhileResizing.current.start - timeDecreasing < 0) {
              return;
            }

            if (props.maxDuration && rangeWhileResizing.current.end - rangeWhileResizing.current.start - valueOffsetWithoutHittingResize.current >= props.maxDuration) {
              return;
            }

            onAutoScroll(-1 * timeDecreasing, 'start');
            timelineContainer.scrollLeft -= pixelsSubtracting;
            valueOffsetWithoutHittingResize.current -= timeDecreasing;
            previousDifference.current = currentIterationDifference;
          }, DEFAULT_VALUE_FOR_TIMEOUT / acceleration);
        }, 50);
      }

      return;
    }

    if (!itemRight) {
      return;
    }

    const isExceedingRightSide = timelineParentClientWidth - itemRight < threshold;
    const isExceedingLeftSide = itemRight < threshold;

    if (!isExceedingRightSide || !isExceedingLeftSide) {
      clearIntervalIfExists();
      clearTimeoutIfExists();
      previousDifference.current = null;
      isResizingOutOfBounds.current = false;
    }

    if (isExceedingLeftSide) {
      isResizingOutOfBounds.current = true;
      const acceleration = getAccelerationFromThreshold(threshold - itemRight);
      rangeWhileResizing.current = range;
      clearIntervalIfExists();
      clearTimeoutIfExists();

      timer.current = setTimeout((): void => {
        interval.current = setInterval((): void => {
          const currentIterationDifference = threshold - itemRight;
          const pixelsSubtracting = 5 + (currentIterationDifference - (previousDifference.current ?? 0));
          const timeDecreasing = pixelsSubtracting / widthPerSecond;

          if (props.minDuration && rangeWhileResizing.current.end - rangeWhileResizing.current.start + valueOffsetWithoutHittingResize.current <= props.minDuration) {
            return;
          }

          onAutoScroll(-1 * timeDecreasing);
          timelineContainer.scrollLeft -= pixelsSubtracting;
          valueOffsetWithoutHittingResize.current -= timeDecreasing;
          previousDifference.current = currentIterationDifference;
        }, DEFAULT_VALUE_FOR_TIMEOUT / acceleration);
      }, 50);
    }

    if (isExceedingRightSide) {
      isResizingOutOfBounds.current = true;

      const acceleration = getAccelerationFromThreshold(threshold - (timelineParentClientWidth - itemRight));
      rangeWhileResizing.current = range;
      clearIntervalIfExists();
      clearTimeoutIfExists();

      timer.current = setTimeout((): void => {
        interval.current = setInterval((): void => {
          const currentIterationDifference = threshold - (timelineParentClientWidth - itemRight);
          const pixelsAdding = 5 + (currentIterationDifference - (previousDifference.current ?? 0));
          const timeAdding = pixelsAdding / widthPerSecond;

          if (props.maxDuration && rangeWhileResizing.current.end - rangeWhileResizing.current.start + valueOffsetWithoutHittingResize.current >= props.maxDuration) {
            return;
          }
          if (props.id !== TIMELINE_DESIGN_ITEM_ID && props.posterDuration && rangeWhileResizing.current.end + valueOffsetWithoutHittingResize.current > props.posterDuration) {
            return;
          }
          onAutoScroll(timeAdding);
          timelineContainer.scrollLeft += pixelsAdding;
          valueOffsetWithoutHittingResize.current += timeAdding;
          previousDifference.current = currentIterationDifference;
        }, DEFAULT_VALUE_FOR_TIMEOUT / acceleration);
      }, 50);
    }
  };

  const onResizeMoveCallback = (moveEvent: ResizeMoveEvent): void => {
    autoScrolling(moveEvent);
    updateTooltipPosition(moveEvent.direction);
    dispatch(updateItemResizeState(true));
    const updatedRange = moveEvent.active.data.current.getSpanFromResizeEvent?.(moveEvent);
    if (!updatedRange) {
      return;
    }
    if (moveEvent.direction === 'end') {
      updatedRange.end += valueOffsetWithoutHittingResize.current;
    } else {
      updatedRange.start += valueOffsetWithoutHittingResize.current;
    }
    onResizeMove(updatedRange, moveEvent.direction);
  };

  const onResizeEndCallback = (event: ResizeEndEvent): void => {
    const newSpan = event.active.data.current.getSpanFromResizeEvent?.(event);
    if (newSpan) {
      rangeWhileResizing.current = newSpan;
      updatedRelevanceWhileResizing.current = newSpan;
    }
    valueOffsetWithoutHittingResize.current = 0;
    clearIntervalIfExists();
    clearTimeoutIfExists();
    removeCursorClass();
    isResizing.current = false;
    onResizeEnd(event);
    dispatch(updateItemResizeState(false));
  };

  const attachCursorClass = (): void => {
    document.body.classList.add(styles.cursor);
  };

  const removeCursorClass = (): void => {
    document.body.classList.remove(styles.cursor);
  };

  const onResizeStartCallback = (event: ResizeStartEvent): void => {
    dispatch(updateItemResizeState(true));
    attachCursorClass();
    if (event.direction === 'start') {
      resizeDirection.current = 'start';
    } else {
      resizeDirection.current = 'end';
    }
    updatedRelevanceWhileResizing.current = event.active.data.current.span;
    updateTooltipPosition(event.direction);
    isResizing.current = true;
    onResizeStart(event);
  };

  const isSmallUneditableBlob = (): boolean => {
    return widthPerSecond * (props.range.end - props.range.start) < 40;
  };

  const isResizing = useRef(false);
  const {setNodeRef, node, isDragging, active, attributes, listeners, itemStyle} = useItem({
    id: props.id,
    span: props.range,
    onResizeMove: onResizeMoveCallback,
    onResizeEnd: onResizeEndCallback,
    onResizeStart: onResizeStartCallback,
    disabled,
    disableDragOnly,
    disableResizeOnly: isSmallUneditableBlob(),
  });

  const updateTooltipPosition = (direction: DragDirection): void => {
    if (direction === 'start') {
      if (!leftHandleRef.current || !node.current) {
        return;
      }
      const {left} = leftHandleRef.current.getBoundingClientRect();
      setTooltipPosition({
        left,
        top: leftHandleRef.current.getBoundingClientRect().top,
      });
    } else {
      if (!rightHandleRef.current || !node.current) {
        return;
      }
      let {left} = rightHandleRef.current.getBoundingClientRect();

      if (left < 0) {
        left = 40;
      }

      setTooltipPosition({
        left,
        top: rightHandleRef.current.getBoundingClientRect().top,
      });
    }
  };

  const getLeftForTooltip = (): number => {
    return tooltipPosition.left - 16;
  };

  const getTopForTooltip = (): number => {
    return tooltipPosition.top - 52;
  };

  const getTooltipText = (): string => {
    if (resizeDirection.current === 'start') {
      let startTime = isResizingOutOfBounds.current ? rangeWhileResizing.current.start + valueOffsetWithoutHittingResize.current : props.range.start;
      if (props.maxDuration && props.range.end - startTime > props.maxDuration) {
        startTime = props.range.end - props.maxDuration;
      }

      if (startTime < 0) {
        startTime = 0;
      }

      return startTime.toFixed(1);
    }
    let endTime = isResizingOutOfBounds.current ? rangeWhileResizing.current.end + valueOffsetWithoutHittingResize.current : props.range.end;
    if (isDesignItem) {
      return endTime.toFixed(1);
    }
    if (props.maxDuration && endTime - props.range.start > props.maxDuration) {
      endTime = props.range.start + props.maxDuration;
    }

    const posterDuration = window.posterEditor?.whiteboard?.getCurrentPage().getDuration();
    if (posterDuration && endTime > posterDuration) {
      endTime = posterDuration;
    }
    return endTime.toFixed(1);
  };

  const isActive = (): boolean => {
    return active?.id === props.id || isResizing.current || isSelected;
  };

  const getClassFromType = (): string => {
    switch (type) {
      case TimelineItemType.Purple:
        return styles.purple;
      case TimelineItemType.Green:
        return styles.green;
      case TimelineItemType.Blue:
      default:
        return styles.blue;
    }
  };

  const getHandleType = (): TimelineItemHandleType => {
    switch (type) {
      case TimelineItemType.Purple:
        return TimelineItemHandleType.Purple;
      case TimelineItemType.Green:
        return TimelineItemHandleType.Green;
      case TimelineItemType.Blue:
      default:
        return TimelineItemHandleType.Blue;
    }
  };

  const getAddButton = (): ReactElement<any> | null => {
    if (showAddButton) {
      return (
        <ClickableDiv
          onClick={(event): void => {
            event.stopPropagation();
          }}
          onPointerUp={(event): void => {
            if (isResizing.current || isDragging) {
              return;
            }
            event.stopPropagation();
            if (!isAddButtonDisabled) {
              onClickAddButton();
            }
          }}
          onPointerDown={(event): void => {
            event.stopPropagation();
          }}
        >
          <Icon isDisabled={isAddButtonDisabled} className={`${styles.addIcon} radius-8`} icon="icon-plus" size={addItemIconSize} type={IconType.SHADOW} shape={IconShape.SQUARE} />
        </ClickableDiv>
      );
    }

    return null;
  };

  const getTimeInSeconds = (): string => {
    return `${props.duration.toFixed(1)}s`;
  };

  useEffect(() => {
    if (!node.current) {
      return noop;
    }

    const hammer = new Hammer.Manager(node.current);
    const tapEvent = new Hammer.Tap({
      taps: 2,
    });

    hammer.add(tapEvent);
    hammer.on('tap', expandBlob);

    return (): void => {
      hammer.destroy();
    };
  }, [node.current]);

  const expandBlob = (event: HammerInput): void => {
    event.preventDefault();
    if (!isSmallUneditableBlob()) {
      return;
    }

    const acceptableWidth = TIMELINE_ITEM_ACCEPTABLE_EDITING_WIDTH / (props.range.end - props.range.start);
    dispatch(updateTimelineWidthPerSecond(acceptableWidth));
    setTimeout((): void => {
      node?.current?.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
    }, 300);
  };

  const getTransition = (): string => {
    if (!isFirstRenderComplete.current) {
      return '';
    }
    if (isAnyItemResizing || isDragging) {
      return '';
    }
    return 'left 0.2s ease-in-out, right 0.2s ease-in-out';
  };

  const getPageNumberNotification = (): ReactElement<any> => {
    return <PageNumberNotification className={`${styles.pageNumberLeft} ${isActive() ? styles.active : ''}`} pageId={props.id} />;
  };

  const getZindex = (): number | undefined => {
    if (isDragging || isResizing.current) {
      return 5;
    }

    if (isActive()) {
      return 2;
    }

    return undefined;
  };

  return (
    <>
      <Tooltip
        style={{
          left: getLeftForTooltip(),
          top: getTopForTooltip(),
        }}
        className={`${styles.tooltip} ${isResizing.current ? '' : '_hidden'}`}
        id="left-handle-tooltip"
        text={<Text size={TextSize.XSMALL} val={getTooltipText()} />}
      />
      <div
        id={props.id}
        onPointerUp={(e): void => {
          listeners.onPointerUp(e);
        }}
        onPointerMove={(e): void => {
          listeners.onPointerMove(e);
          e.preventDefault();
        }}
        onPointerDown={(e): void => {
          onClick(props.id);
          listeners.onPointerDown(e);
          e.preventDefault();
        }}
        ref={setNodeRef}
        style={{
          ...itemStyle,
          display: isHidden ? 'none' : '',
          transition: getTransition(),
          zIndex: getZindex(),
          minWidth: `${props.minDuration ? widthPerSecond * props.minDuration : undefined}px`,
          maxWidth: `${props.maxDuration ? widthPerSecond * props.maxDuration : undefined}px`,
        }}
        {...attributes}
      >
        {isSmallUneditableBlob() && !isResizing.current ? (
          <div className={`_full-width _full-height radius-8 ${styles.blob} ${isSelected ? styles.active : ''} ${getClassFromType()}`} />
        ) : null}
        <div
          className={`${styles.container} ${isSmallUneditableBlob() && !isResizing.current ? '_hidden' : ''} ${isDragging ? styles.dragging : ''} radius-8 _full-height _full-width _overflow-hidden ${getClassFromType()} ${isActive() ? styles.active : ''} hover-transition-all flex-h-row flex-items-center`}
        >
          <TimelineItemHandle
            id={TimelineHandleSide.Left}
            activeType={getHandleType()}
            isActive={isActive()}
            className={`${styles.leftHandle} ${isDragging ? '_hidden' : ''}`}
            ref={leftHandleRef}
            side={TimelineHandleSide.Left}
          />
          <TimelineItemHandle
            id={TimelineHandleSide.Right}
            activeType={getHandleType()}
            isActive={isActive()}
            className={`${styles.rightHandle} ${isDragging ? '_hidden' : ''}`}
            ref={rightHandleRef}
            side={TimelineHandleSide.Right}
          />
          {isDesignItem && isMultiPageEnabled ? getPageNumberNotification() : null}
          <DurationNotification className={styles.secondsNotificationContainer} type={durationItemType} duration={getTimeInSeconds()} />
          {props.children}
        </div>
        {getAddButton()}
      </div>
    </>
  );
}
